import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Chip, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Link from '@mui/material/Link';

import { getUsageTerm, getUsageTermOptions, UsageChartType, UsageQueryParams, UsageTerm, UsageTermOption } from "../ApiUsagePage";
import { InfegyUser } from "system/InfegyUser";
import APIRequest from "system/APIRequest";
import UsageBarChart, { getNonEmptyUsageCategories, sortUsageCategories } from "routes/apiUsage/components/charts/UsageBarChart";
import { UsageDisplayMetric } from "../ApiUsagePage";
import { TextLoader } from "components/apiHandling/TextLoader";
import RollingUsageBarChart from "routes/apiUsage/components/charts/AccumulatedUsageBarChart";
import TokenUsageDataGrid from "routes/apiUsage/components/TokenUsageDataGrid";
import { TokenUsage, TotalUsage, Usage, UsageCategoryKey, UsageMetric } from "../types/Usage";
import UsageCards from "../components/UsageCards";
import DateRangeLabel from "../components/DateRangeLabel";

export interface UsageApiToken extends TotalUsage {
  id: string;
  description: string;
}

interface UserUsageProps {
  currentTerm: UsageTerm;
  user?: InfegyUser | null;
  visible: boolean;
}

export default function UserUsage(props: UserUsageProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const usageTermOptions = getUsageTermOptions(i18n);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [usageDisplayMetric, setUsageDisplayMetric] = useState<UsageMetric>("cost");
  const [selectedUsageTermOption, setSelectedUsageTermOption] = useState<UsageTermOption>(usageTermOptions[0]);
  const [activeUsageChart, setActiveUsageChart] = useState<UsageChartType>(UsageChartType.PeriodicUsage);
  const [selectedApiToken, setSelectedApiToken] = useState<UsageApiToken | null>(null);

  const [userUsage, setUserUsage] = useState<Usage | null>(null);
  const [hasAnyUsage, setHasAnyUsage] = useState<boolean>(false);
  const [usageCategoryOptions, setUsageCategoryOptions] = useState<Array<UsageCategoryKey>>([]);
  const [selectedUsageCategories, _setSelectedUsageCategories] = useState<Array<UsageCategoryKey>>([]);

  function setSelectedUsageCategories(categories: Array<UsageCategoryKey>) {
    let sortedCategories = categories;
    if (userUsage) {
      sortUsageCategories(categories, userUsage, usageDisplayMetric, i18n);
    }
    _setSelectedUsageCategories(sortedCategories);
  }

  async function getUserUsage(usageTerm: UsageTerm, options: {tokenId?: string, userId?: string}): Promise<Usage> {
    const queryParams: UsageQueryParams = {
      since: usageTerm.term_start,
      until: usageTerm.term_end
    };
    if (options.tokenId) {
      queryParams.token_id = options.tokenId;
    } else if (options.userId) {
      queryParams.user_id = options.userId;
    }
    const response: Usage = await APIRequest("usage", queryParams) as Usage;
    return response;
  }

  async function load(apiToken: UsageApiToken | null) {
    setIsLoading(true);
    setLoadError("");
    setUserUsage(null);
    setHasAnyUsage(false);
    setUsageCategoryOptions([]);
    setSelectedUsageCategories([]);
    
    try {
      const activeUsageTerm = getUsageTerm(selectedUsageTermOption.type, props.currentTerm);

      const usage = await getUserUsage(activeUsageTerm, {tokenId: apiToken?.id, userId: props.user?.id});
      const usageBuckets = Object.keys(usage.total.count);
      const hasAnyUsage = !!usageBuckets.length;

      let usageCategoryOptions = getNonEmptyUsageCategories(usage, usageDisplayMetric);
      usageCategoryOptions = sortUsageCategories(usageCategoryOptions, usage, usageDisplayMetric, i18n);

      setSelectedApiToken(apiToken);
      setUserUsage(usage);
      setUsageCategoryOptions(usageCategoryOptions);
      setHasAnyUsage(hasAnyUsage);
      setSelectedUsageCategories(usageCategoryOptions);
    } catch (error: any) {
      if (error.code) {
        // This was an error from the API.  Display it as such.
        setLoadError(`${error.message}: ${error.server_message}`);
      } else {
        // This was an error from the UI.
        setLoadError(`${i18n.t("api-usage-page.error-loading")} ${error}`);
      }
    }

    setIsLoading(false);
  }

  useEffect(() => {
    load(selectedApiToken);
  }, [props.currentTerm, props.user, selectedUsageTermOption]);

  const tokens: Array<TokenUsage> = userUsage?.users?.[0]?.tokens || [];
  const hasApiToken = !!tokens.length;

  const whoseUsageLabel = props.user ? `${props.user?.name}'s` : i18n.t("your");

  return (
    <Stack
      sx={{
        display: props.visible ? "auto" : "none",
        gap: 1
      }}>
      {isLoading &&
        <TextLoader />
      }
      {!isLoading &&
        <>
          <Box>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                justifyContent: "flex-end"
              }}>
              <Typography variant="h3" sx={{
                width: "100%"
              }}>{i18n.t("user-usage.whose-usage-heading", {whose: whoseUsageLabel})}</Typography>
              {!loadError &&
                <>
                  <ToggleButtonGroup
                    exclusive={true}
                    onChange={(event, value: UsageDisplayMetric) => value && setUsageDisplayMetric(value)}
                    value={usageDisplayMetric}>
                    <ToggleButton size="small" value="cost">{i18n.t("usage.metrics.cost")}</ToggleButton>
                    <ToggleButton size="small" value="count">{i18n.t("usage.metrics.count")}</ToggleButton>
                    <ToggleButton size="small" value="price">{i18n.t("usage.metrics.price")}</ToggleButton>
                  </ToggleButtonGroup>
                  <Autocomplete
                    disableClearable
                    getOptionLabel={(option) => option.label}
                    options={usageTermOptions}
                    onChange={(event, value) => setSelectedUsageTermOption(value) }
                    renderInput={(params) => <TextField autoComplete="off" {...params} />}
                    renderOption={(props, option, state) => {
                      const unkeyedProps: any = {...props};
                      delete unkeyedProps["key"];
                      return (
                        <li
                          key={props.key}
                          {...unkeyedProps}
                          style={{
                            ...props.style,
                            ...(option.type === selectedUsageTermOption.type && {backgroundColor: theme.colors.primary.light, fontWeight: "bold"})
                          }}
                        >
                          {option.label}
                        </li>
                      )
                    }}
                    size="small"
                    sx={{width: 400}}
                    value={selectedUsageTermOption}
                  />
                </>
              }
            </Stack>
            {!loadError && userUsage &&
              <DateRangeLabel end={userUsage.until} start={userUsage.since} />
            }
          </Box>
        </>
      }
      {!isLoading && loadError &&
        <Alert severity="error" variant="outlined">{loadError}</Alert>
      }
      {!isLoading && !loadError &&
        <>
          {selectedApiToken &&
            <Alert
              severity="warning"
              sx={{
                "& .MuiAlert-icon": {
                  alignItems: 'center'
                },
                mt: 1,
                pt: 1, pb: 1,
              }}
              variant="outlined"
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 1
                }}>
                <Typography>{i18n.t("user-usage.filtered-to-api-token")}</Typography>
                <Chip
                  color="primary"
                  label={selectedApiToken.description}
                  onDelete={() => load(null)}
                  variant="outlined"
                />
              </Stack>
            </Alert>
          }
          {userUsage &&
            <>
              {!hasAnyUsage &&
                <Alert severity="info" variant="outlined">{i18n.t("user-usage.no-api-usage-info")}</Alert>
              }
              {hasAnyUsage &&
                <>
                  <ToggleButtonGroup
                    exclusive={true}
                    onChange={(event, value: UsageChartType) => setActiveUsageChart(value)}
                    value={activeUsageChart}>
                    <ToggleButton size="small" value={UsageChartType.PeriodicUsage}>{i18n.t("user-usage.toggle-periodic-usage")}</ToggleButton>
                    <ToggleButton size="small" value={UsageChartType.AccumulatedUsage}>{i18n.t("user-usage.toggle-accumulated-usage")}</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{
                    display: activeUsageChart === UsageChartType.PeriodicUsage ? "auto" : "none"
                  }}>
                    <UsageBarChart 
                      metric={usageDisplayMetric}
                      onChange={(categories) => setSelectedUsageCategories(categories)}
                      selectedUsageCategories={selectedUsageCategories}
                      usage={userUsage}
                      usageCategoryOptions={usageCategoryOptions}
                    />
                  </Box>
                  <Box sx={{
                    display: activeUsageChart === UsageChartType.AccumulatedUsage ? "auto" : "none"
                  }}>
                    <RollingUsageBarChart
                      metric={usageDisplayMetric}
                      onChange={(categories) => setSelectedUsageCategories(categories)}
                      selectedUsageCategories={selectedUsageCategories}
                      usage={userUsage}
                      usageCategoryOptions={usageCategoryOptions}
                    />
                  </Box>
                </>
              }
              <Box><i>
                {i18n.t("user-usage.usage-endpoint-info")}&nbsp;
                <Link href={"https://starscape.infegy.com/api/docs/paths/usage/get"} target="_blank" rel="noreferrer" >
                  {i18n.t("user-usage.usage-endpoint-link")}
                </Link>
                .
              </i></Box>
              <Typography variant="h3">{i18n.t("user-usage.usage-categories-heading")}</Typography>
              <UsageCards metric={usageDisplayMetric} usage={userUsage} />
              <Typography variant="h3">{i18n.t("user-usage.api-token-usage-heading")}</Typography>
              {!hasApiToken &&
                <Alert severity="info" variant="outlined">{i18n.t("user-usage.no-api-tokens-info")}</Alert>
              }
              {hasApiToken && props.visible &&
                <TokenUsageDataGrid
                  onClick={(token) => load(token)}
                  tokenUsage={tokens}
                  usageDisplayMetric={usageDisplayMetric}
                />
              }
            </>
          }
        </>
      }
    </Stack>
  );
}